'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchMe, type AuthUser, type OrgMembership } from '@/lib/auth';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  currentOrg: OrgMembership | null;
  setCurrentOrg: (org: OrgMembership | null) => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  refresh: async () => {},
  currentOrg: null,
  setCurrentOrg: () => {},
});

const ORG_STORAGE_KEY = 'preview-mr-current-org';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentOrg, setCurrentOrgState] = useState<OrgMembership | null>(null);

  const setCurrentOrg = useCallback((org: OrgMembership | null) => {
    setCurrentOrgState(org);
    if (org) {
      localStorage.setItem(ORG_STORAGE_KEY, org.org_slug);
    } else {
      localStorage.removeItem(ORG_STORAGE_KEY);
    }
  }, []);

  const refresh = async () => {
    const u = await fetchMe();
    setUser(u);

    if (u && u.organizations.length > 0) {
      const savedSlug = localStorage.getItem(ORG_STORAGE_KEY);
      const savedOrg = savedSlug
        ? u.organizations.find((o) => o.org_slug === savedSlug)
        : null;
      setCurrentOrgState(savedOrg || u.organizations[0]);
    } else {
      setCurrentOrgState(null);
    }

    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, currentOrg, setCurrentOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
